<template>
  <div class="payment-request-action-column">
    <WdsMenu z-index="1000" placement="bottom-end">
      <template #trigger>
        <WdsButtonGhost :label="$t('More options')" data-test="payment-request-actions-menu">
          <WdsIcon name="overflow-menu-horizontal" />
        </WdsButtonGhost>
      </template>

      <WdsMenuOption data-test="see-worker-details-option" @click="seeWorkerDetails">
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="person" :size="16" />
          {{ $t('See worker details') }}
        </WdsStack>
      </WdsMenuOption>

      <WdsMenuDivider />

      <WdsMenuOption
        theme="SUCCESS"
        :disabled="isProcessingBatchAction"
        data-test="mark-payrolled-option"
        @click="markPayrolled"
      >
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon v-if="!isProcessingBatchAction" name="checkmark-outline" :size="16" />
          <WdsSpinner v-else :size="16" />
          {{ isProcessingBatchAction ? $t('Marking as payrolled...') : $t('Confirm payrolled') }}
        </WdsStack>
      </WdsMenuOption>

      <WdsMenuOption data-test="export-option" @click="exportBatch">
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="download" :size="16" />
          {{ $t('Export') }}
        </WdsStack>
      </WdsMenuOption>

      <WdsMenuOption theme="DESTRUCTIVE" data-test="undo-processing-option" @click="undoProcessing">
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="undo" :size="16" />
          {{ $t('Undo processing') }}
        </WdsStack>
      </WdsMenuOption>
    </WdsMenu>
  </div>
</template>

<script lang="ts" setup>
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useToast } from '@/composables/useToast'
import { useTablePanels } from '@/components/GraphqlDataTable/useTablePanels'
import { RunBatchActionMutation } from '@/components/GraphQL/mutations/Batch/RunBatchActionMutation'
import { BatchActionType } from '@/gql/graphql'
import { downloadFromUrls, getBatchIdFromRow } from '@/components/PaymentRequests/Partner/composables/batchActions'
import { FragmentType, graphql, useFragment } from '@/gql'

const { openPartnerWorkerDetailPanel } = useTablePanels()

const PaymentRequestActionColumnProcessing = graphql(`
  fragment PaymentRequestActionColumnProcessing on PaymentRequest {
    id
    batches {
      data {
        id
      }
    }
    worker {
      id
    }
    hire {
      employment {
        id
      }
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof PaymentRequestActionColumnProcessing>
}>()

const column = computed(() => useFragment(PaymentRequestActionColumnProcessing, props.row))

const toast = useToast()

const emit = defineEmits<{
  'payment-request-updated': []
}>()

const { mutate: runBatchAction, loading: isProcessingBatchAction } = useMutation(
  RunBatchActionMutation,
  {
    clientId: 'publicApi',
  },
)

const seeWorkerDetails = () => {
  openPartnerWorkerDetailPanel({
    workerId: column.value.worker.id,
    employmentId: column.value.hire?.employment?.id,
  })
}

const markPayrolled = async () => {
  try {
    const batchId = getBatchIdFromRow(column.value)
    if (!batchId) {
      toast.error($t('No batch found for this payment request.'))
      return
    }
    await runBatchAction({
      input: {
        batch: batchId,
        action: BatchActionType.Process,
        scope: { ids: [column.value.id] },
        deleteIfEmptied: false,
      },
    })

    toast.success($t('Successfully marked payment request as payrolled'))
    emit('payment-request-updated')
  } catch (error) {
    toast.error($t('Failed to mark payment request as payrolled. Please try again.'))
  }
}

const exportBatch = async () => {
  try {
    const batchId = getBatchIdFromRow(column.value)
    if (!batchId) {
      toast.error($t('No batch found for this payment request.'))
      return
    }
    const res = await runBatchAction({
      input: {
        batch: batchId,
        action: BatchActionType.Export,
        scope: { ids: [column.value.id] },
        deleteIfEmptied: false,
      },
    })
    const url = res?.data?.runBatchAction?.resultUrl
    await downloadFromUrls([url])
  } catch (error) {
    toast.error($t('Failed to export payment request. Please try again.'))
  }
}

const undoProcessing = async () => {
  try {
    const batchId = getBatchIdFromRow(column.value)
    if (!batchId) {
      toast.error($t('No batch found for this payment request.'))
      return
    }
    await runBatchAction({
      input: {
        batch: batchId,
        action: BatchActionType.Undo,
        scope: { ids: [column.value.id] },
        deleteIfEmptied: true,
      },
    })

    toast.success($t('Successfully undone processing'))
    emit('payment-request-updated')
  } catch (error) {
    toast.error($t('Failed to undo processing. Please try again.'))
  }
}
</script>

<style scoped lang="scss">
.payment-request-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
